import React from 'react';
import { KbSection, Callout, StepList, VisibleTo } from '../../components/knowledge/primitives.jsx';
import CommentToggleDemo from '../../components/knowledge/demos/CommentToggleDemo.jsx';
import AttachmentSlotDemo from '../../components/knowledge/demos/AttachmentSlotDemo.jsx';
import RequirementsDemo from '../../components/knowledge/demos/RequirementsDemo.jsx';
import TagComboDemo from '../../components/knowledge/demos/TagComboDemo.jsx';
import CostLineDemo from '../../components/knowledge/demos/CostLineDemo.jsx';

// "Working a case" articles. Gates vary per article — this category mixes
// universal features (comments) with tier-restricted ones (private comments).

function CommentsBody({ user }) {
  return (
    <>
      <KbSection title="The conversation on a case">
        <p>
          Every case carries a comment thread on its Overview tab — the running record of
          decisions, questions and follow-ups. Anyone who can open the case can read the
          thread; each comment shows its author and when it was posted, and files attached
          to a comment appear as pills underneath it. The thread only ever shows what your
          role is allowed to read — where discussion is restricted to other roles, it
          simply doesn't appear.
        </p>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canComment}>
        <KbSection title="Posting a comment" delay={1}>
          <StepList steps={[
            <>Write in the composer at the top of the Comments panel. The toolbar gives you <strong>bold</strong>, bullet and numbered lists, and a small set of brand colours.</>,
            <>Optionally use <strong>+ Add</strong> to attach a file or line up an email notification (below).</>,
            <>Press <strong>Post comment</strong> — it lands in the thread immediately.</>,
          ]} />
          <Callout tone="info">
            The thread is part of the case record — write comments as you'd want them read
            back in a review.
          </Callout>
        </KbSection>

        <KbSection title="Attaching files, requesting, notifying" delay={2}>
          <p>The <strong>+ Add</strong> menu on the composer lines things up to go out with the comment:</p>
          <StepList steps={[
            <><strong>Attachment</strong> — pick a category first (the case's required documents are listed at the top), then the file. It's renamed to the case's standard file name on upload and shows on the case like any other file.</>,
            user?.canManageRequirements && <><strong>Requirement</strong> — ask for a document or a sign-off; posting adds the slot to the checklist.</>,
            <><strong>Notification</strong> — choose recipients (the case's Clinic Manager and Head of RO are offered first, plus colleagues by team or any staff email) and an optional message. The email goes out when you post the comment, with a link back to the case.</>,
            user?.canManageSla && <><strong>Snooze SLA notifications</strong> — quiet the response-clock reminders for a number of days once the clinic acknowledges this message.</>,
          ].filter(Boolean)} />
          <p>
            A Supervisor's message on a coded treatment case also carries an{' '}
            <strong>Acknowledge</strong> button for the clinic — see <em>Response SLA and
            acknowledgements</em>.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function PrivateCommentsBody() {
  return (
    <>
      <KbSection title="What a private comment is">
        <p>
          Comments on a case are <strong>Public</strong> by default — anyone who can open the
          case can read them. Switching the composer to <strong>Private</strong> restricts the
          comment to HoRO, Admin and medical roles: use it for clinical reasoning, escalation
          notes or anything not intended for the clinic team.
        </p>
        <CommentToggleDemo />
      </KbSection>

      <KbSection title="Posting one" delay={1}>
        <StepList steps={[
          <>Write your comment in the composer on the case page.</>,
          <>Flip the visibility toggle from <strong>Public</strong> to <strong>Private</strong> — it turns amber.</>,
          <>Post. The comment shows an amber edge in the thread so its visibility is always obvious.</>,
        ]} />
        <Callout tone="warn">
          Visibility is fixed at posting time — a comment cannot be switched between Public and
          Private later. If in doubt, start Private.
        </Callout>
      </KbSection>

      <KbSection title="Who sees what" delay={2}>
        <p>
          Clinic Managers see only public comments — private discussion is filtered out
          entirely, with nothing to indicate it exists. Comment history migrated from the
          old SharePoint tracker is always private.
        </p>
      </KbSection>
    </>
  );
}

function EditCaseFileBody() {
  return (
    <>
      <KbSection title="Which panels you can edit">
        <p>
          Case details aren't frozen at submission. On the case Overview, these panels carry
          an <strong>Edit</strong> button in their header:
        </p>
        <StepList steps={[
          <><strong>Incident details</strong> — date of incident, treatment, category and the client fields for a treatment case; the equivalent field set for a near miss or clinic incident.</>,
          <><strong>Notes</strong> — rewrite the notes, or use <strong>Add</strong> to append a new dated note without touching what's already there.</>,
          <><strong>Case information</strong> — severity, clinic, region and country.</>,
          <><strong>Staff</strong> — staff member concerned, responsible Clinic Manager, medical team member.</>,
        ]} />
      </KbSection>

      <KbSection title="How an edit works" delay={1}>
        <StepList steps={[
          <>Click <strong>Edit</strong> on the panel — a dialog opens pre-filled with the current values.</>,
          <>Change what you need and press <strong>Save changes</strong>.</>,
          <>Confirm. Every save asks first, and every change lands in the case history with who and when — there are no silent edits.</>,
        ]} />
      </KbSection>

      <KbSection title="Status and severity" delay={2}>
        <p>
          These change from the badges in the case header, not from a panel: the
          <strong> Severity</strong> and <strong>Status</strong> badges open as dropdowns — click
          one and pick the new value.
          Severity applies only to coded treatments (Code 1–5). You'll notice
          <strong> Closed</strong> is missing from the status list — closing always goes through
          <em> Mark resolved</em>, so every closed case carries a short closing summary. Once a
          case is Closed, only someone with reopen rights can change its status again.
        </p>
        <Callout tone="warn">
          Changing the treatment or severity updates the required-documents checklist —
          slots can appear or disappear to match the new combination. Files already uploaded
          are never removed.
        </Callout>
      </KbSection>
    </>
  );
}

function AttachmentsBody({ user }) {
  return (
    <>
      <KbSection title="Two homes for files">
        <p>
          Files live in two places on the case Overview. <strong>Required documents &amp;
          sign-offs</strong> lists the uploads this case must have — one slot per document,
          driven by the treatment and severity, plus any per-case requests. The
          <strong> Attachments</strong> panel below holds everything else. Click a file to
          preview it in place — images, PDFs, Word, Excel and plain-text files open right in
          the app; anything else downloads.
        </p>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canWriteCases}>
        <KbSection title="Filling a required slot" delay={1}>
          <StepList steps={[
            <>Each slot keeps its actions on the right. Press <strong>Upload</strong> — your file picker opens with the category already decided.</>,
            <>The file is renamed automatically so every case file follows the same pattern — e.g. <em>AE42-ConsentForm.pdf</em>. A second file in the same slot gets <em>-2</em>, a third <em>-3</em>. The original filename isn't kept.</>,
            <>The slot's icon turns <strong>green</strong> once a file has arrived and the button reads <strong>Add more</strong> for further files in the same category. A slot answered without a file (below) shows <strong>amber</strong> instead.</>,
          ]} />
          <AttachmentSlotDemo />
          <Callout tone="info">
            If the card says <em>Read-only for your role</em>, add files through the comment
            composer instead — a file posted with the matching category lands in the right slot.
          </Callout>
        </KbSection>

        <VisibleTo user={user} when={u => !!u?.canWaiveRequirements}>
          <KbSection title="Recording that there is no consent form" delay={2}>
            <p>
              Sometimes the document doesn't exist — the client declined to sign, or the form was
              never taken. Rather than leave the slot open, press <strong>Record no consent
              form</strong> on the slot and give the reason. The slot turns <strong>amber</strong>{' '}
              with a dash-circle icon: the requirement is answered, but everyone can see it was
              answered without the file. If the form turns up later, <strong>Withdraw</strong> the
              record and upload it; a Supervisor can also clear the record and ask again.
            </p>
          </KbSection>
        </VisibleTo>

        <KbSection title="Limits and allowed types" delay={3}>
          <p>
            Uploads are checked before anything is sent: <strong>50&nbsp;MB per file</strong>,
            and only the kinds of file you'd expect as case evidence — documents (PDF, Word,
            Excel, CSV, TXT, RTF), images (JPG, PNG, HEIC, WebP and similar) and short video
            clips (MP4, MOV, M4V). Anything else is turned away with a message saying why.
          </p>
        </KbSection>

        <KbSection title="Deleting files" delay={3}>
          <p>
            Both the required-documents card and the Attachments panel have an edit mode where
            your role can manage the case file: press <strong>Edit</strong>, tick the files to
            remove, then <strong>Delete&nbsp;n</strong> and confirm. Deletions are logged in the
            case history like any other change.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function ResponseSlaBody({ user }) {
  return (
    <>
      <KbSection title="What the response SLA is">
        <p>
          An <strong>SLA</strong> (service-level agreement) is simply a promise about how quickly
          a message on a case gets an answer. Adverse events matter most in the first hours and
          days: the client is waiting, the clinic needs guidance, and the details are freshest.
          Without an agreed response time a case can sit quietly with nobody sure whose move it
          is. The response clock makes that promise visible on every case, so both sides know
          when a reply is due, reminders go out before a case is forgotten, and the history
          shows the case was handled on time.
        </p>
      </KbSection>

      <KbSection title="Whose turn it is" delay={1}>
        <p>
          Every treatment case with a severity code (Code 1 to 5) carries a <strong>response
          clock</strong> between the Supervisor team and the clinic. When a Supervisor posts on
          the case, the clinic's turn starts; when the clinic replies, it becomes the
          Supervisor's turn. Each turn is measured in <strong>working hours</strong>: 6 for
          Code 4 and 5, 24 for Code 1 to 3 (your admin can change both). The chip on the
          clocks row of the case header always says whose turn it is and either the deadline
          or, inside the last 12 hours, the time left.
        </p>
      </KbSection>

      <KbSection title="Acknowledge" delay={2}>
        <StepList steps={[
          <>A message from the Supervisor side — a Supervisor, HoRO, Country Manager or Admin — shows an <strong>Acknowledge</strong> button underneath it. Press it to record that the message has been seen — your name and the time go on the case. Your own messages and those from clinic colleagues have no button: acknowledging is for what head office sends you.</>,
          <>Acknowledging <strong>pauses the clock</strong> and the reminder emails. The chip reads <em>Waiting on Supervisor</em>: it is now their call to resume the clock, snooze it, or ask for further action, which restarts your window.</>,
          <>Acknowledging is not a reply. If the message asks for something, post your answer or upload the file as usual — that hands the turn back.</>,
        ]} />
        <Callout tone="info">
          Uploading a document on its own does not move the clock — only messages and
          acknowledgements do — so add a short comment when you send a file.
        </Callout>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canManageSla}>
        <KbSection title="Managing the clock" delay={3}>
          <p>
            The <strong>Manage</strong> menu next to the chip is yours. <strong>Resume the clinic
            clock</strong> lifts a hold and lets the clinic's turn run on. <strong>Further action
            required</strong> restarts the clinic's full window. <strong>Suppress reminders</strong>{' '}
            stops the emails for this case with a note that goes into the history. Setting the
            case to <em>Waiting on Client</em> pauses the clock until the status changes back.
          </p>
        </KbSection>

        <KbSection title="Snoozing reminders" delay={4}>
          <StepList steps={[
            <>In the comment composer choose <strong>+ Add → Snooze SLA notifications</strong> and pick a number of days (1 to 30).</>,
            <>Post the comment. When the clinic acknowledges <em>that</em> message the clock is pushed out by the snooze, instead of being held — reminders stay quiet until then.</>,
            <>Only a live snooze shows on the case, as a pill on the comment and in the chip: <em>Snoozed until …</em>.</>,
          ]} />
        </KbSection>

        <KbSection title="Breaches" delay={5}>
          <p>
            When a turn runs past its deadline the chip turns red and starts counting how far
            over it is, in <strong>working hours</strong> — <em>Clinic response overdue by 3h</em>.
            The deadline itself doesn't move; the case simply keeps building overdue time until it
            is answered, and a reminder email goes to the side whose turn it is roughly every turn
            length until then. Every hold, resume, snooze and breach is written to the case history,
            so the record explains itself later.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function RequirementsBody() {
  return (
    <>
      <KbSection title="Two kinds of checklist item">
        <p>
          The <strong>Required documents &amp; sign-offs</strong> card mixes two kinds of item.
          <em> Automatic items</em> come with the case's treatment and severity — they can't be
          removed, only completed. <em>Per-case items</em> are the ones you add yourself:
          a document request picked from the list (or <em>Other</em> with a description), or a
          free-text <strong>sign-off task</strong> someone must complete.
        </p>
      </KbSection>

      <KbSection title="Adding a requirement" delay={1}>
        <StepList steps={[
          <>In the comment composer choose <strong>+ Add → Requirement</strong>.</>,
          <>Pick <strong>Request Document</strong> and a document type — or <strong>Request Sign-off</strong> and describe the task.</>,
          <><strong>Add to comment</strong>, then <strong>Post comment</strong>. Posting creates the slot on the checklist and records the request in the thread.</>,
          <>Requesting doesn't email anyone by itself — add <strong>+ Add → Notification</strong> to the same comment to tell the person you expect to act.</>,
        ]} />
        <RequirementsDemo />
      </KbSection>

      <KbSection title="Fulfilling vs managing" delay={2}>
        <p>
          Anyone who can update cases can <em>complete</em> an item — upload into a document
          slot, or mark a sign-off done with an optional note. <em>Managing</em> the list is
          where your role comes in: each per-case item shows a <strong>Remove</strong> button
          (with confirmation), and removing a requirement never deletes files already uploaded
          against it.
        </p>
        <Callout tone="info">
          When a sign-off is completed, the person who requested it is emailed automatically —
          no chasing needed.
        </Callout>
      </KbSection>

      <KbSection title="When the checklist changes by itself" delay={3}>
        <p>
          Automatic items follow the case: change the treatment or severity and the required
          uploads update to match the new combination. Per-case items are yours — they stay
          until completed or removed.
        </p>
      </KbSection>
    </>
  );
}

function TagsBody({ user }) {
  return (
    <>
      <KbSection title="What tags are for">
        <p>
          Tags are little coloured labels on the case header — handy for grouping cases by
          anything you like: an audit, a campaign, "follow-up". Tags with a small lock are
          added automatically by the app (e.g. <em>sp_migrated</em> marks every case brought
          over from the old SharePoint tracker) — nobody can add or remove those by hand.
        </p>
      </KbSection>

      <KbSection title="Filtering by tag" delay={1}>
        <p>
          In All Cases, pick one or more tags in the filters to narrow the list. Tag filters
          work together with status, clinic and the rest — and they're part of the page's
          link, so you can copy the address to share the exact view you're looking at.
        </p>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canAssignTags}>
        <KbSection title="Adding and removing tags" delay={2}>
          <StepList steps={[
            <>Click <strong>+ Add tag</strong> under the case actions and start typing — the picker shows matching tags from the shared list (arrow keys and Enter work too).</>,
            <>Click a match to apply it.</>,
            <>Remove a tag with the <strong>×</strong> on its pill. Locked tags have no × — they can't be removed.</>,
          ]} />
          <TagComboDemo />
          <VisibleTo user={user} when={u => u?.canAssignTags && !u?.canCreateTags}>
            <Callout tone="info">
              Can't find the tag you need? New tags are created by Supervisors and Super
              Admins — ask them to add it, then it'll be there for every case.
            </Callout>
          </VisibleTo>
        </KbSection>
      </VisibleTo>

      <KbSection title="Filtering the list by tag" delay={3}>
        <p>
          In <strong>All Cases</strong>, the <strong>Tags</strong> filter opens with the{' '}
          <strong>+</strong> beside its title. Every tag has three positions: <strong>−</strong>{' '}
          (cases must <em>not</em> carry it), the dot (not filtering), and <strong>+</strong>{' '}
          (cases must carry it). Press <strong>OK</strong> and your picks appear as pills under
          the title — teal for has, red for doesn't have — each with its own × to remove. With
          several + tags, a case needs any one of them; a − tag always excludes.
        </p>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canCreateTags}>
        <KbSection title="Creating a new tag" delay={4}>
          <p>
            Creating brand-new tags is reserved for <strong>Supervisors and Super Admins</strong> —
            everyone else picks from the existing list. If nothing matches what you typed, the
            picker offers <strong>Create "…"</strong>: that adds the new tag to the shared list,
            ready to use on every case — keep labels short, lowercase and reusable.
          </p>
          <Callout tone="warn">
            Check for near-duplicates before creating ("follow-up" vs "followup") — a tidy
            tag list is what keeps filtering useful.
          </Callout>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function ResolveCloseBody() {
  return (
    <>
      <KbSection title="When a case is ready to close">
        <p>
          Resolve once the outcome is known and the work is done: actions completed, required
          documents uploaded, sign-offs finished. Closing isn't a status you pick —
          <strong> Closed</strong> is deliberately absent from the status dropdown, so every
          closure passes through the resolve flow and captures a narrative.
        </p>
      </KbSection>

      <KbSection title="The resolve flow" delay={1}>
        <StepList steps={[
          <>Press <strong>Mark resolved</strong> in the case header (shown while the case is open).</>,
          <>Describe the resolution — outcome, actions taken, follow-up agreed. The field is required; a case can't close without it.</>,
          <>Press <strong>Close case</strong>. The status becomes Closed and the text is stored on the record and logged in history.</>,
        ]} />
      </KbSection>

      <KbSection title="After closing" delay={2}>
        <p>
          A <strong>Case resolution</strong> card appears on the Overview with the narrative
          and the closed-on date. The status control locks for roles without reopen rights,
          while everything else stays readable — including the comment thread, which remains
          open for late follow-ups.
        </p>
        <Callout tone="info">
          Write the resolution for a future reader — it's the first thing a reviewer, auditor
          or colleague sees on a closed case.
        </Callout>
      </KbSection>
    </>
  );
}

function ReopenDeleteRestoreBody({ user }) {
  return (
    <>
      <VisibleTo user={user} when={u => !!u?.canReopenCase}>
        <KbSection title="Reopening a closed case">
          <p>
            A closed case isn't locked forever — new information, a returning client, a document
            that changes the picture. On the closed case, click the <strong>Status</strong> badge
            and pick the status to reopen into; you'll be asked to confirm
            ("Reopen case — change status from Closed to …?") and the reopen is logged in
            history. Colleagues without reopen rights see a note telling them to ask in a
            comment — so watch the thread on cases you've closed.
          </p>
        </KbSection>
      </VisibleTo>

      <VisibleTo user={user} when={u => !!u?.canDeleteCase}>
        <KbSection title="Deleting a case" delay={1}>
          <p>
            <strong>Delete</strong> in the case header is for cases that shouldn't exist —
            duplicates, test submissions, the wrong clinic. It's a <em>soft</em> delete: the
            case disappears from every list and search, but the record keeps who deleted it and
            when, and it stays restorable for 90 days.
          </p>
          <Callout tone="warn">
            Deleting removes the case from view for everyone, immediately. If the case is real
            but wrong, edit it instead.
          </Callout>
        </KbSection>
      </VisibleTo>

      <VisibleTo user={user} when={u => !!u?.isSuperAdmin}>
        <KbSection title="The restore bin" delay={2}>
          <StepList steps={[
            <>In All Cases, click the red <strong>Deleted</strong> chip above the table — it switches the list into the bin view of soft-deleted cases.</>,
            <>Open one: a banner shows when it was deleted and by whom — "Restorable for 90 days after deletion."</>,
            <>Press <strong>Restore case</strong> on the banner and confirm. The case returns to the normal lists and the restore is logged in history.</>,
          ]} />
          <p>
            After 90 days a deleted case can no longer be restored — the window is enforced
            server-side, not just hidden in the UI.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function CostsBody({ user }) {
  return (
    <>
      <KbSection title="Cost lines on a case">
        <p>
          The <strong>Costs</strong> tab holds one line per expense: the date incurred, a
          category pill (e.g. Refund, Goodwill, Legal — your admin may have configured
          different values), the amount in its currency, optional notes and who added it.
          The total in the header adds each currency separately — €500 + £200 stays
          "€500 · £200"; nothing is ever converted.
        </p>
        <CostLineDemo />
      </KbSection>

      <KbSection title="Where costs show up" delay={1}>
        <p>
          Beyond the tab: a Cost column in All Cases, cost figures on the dashboard, and cost
          events in the case history — all showing the same unconverted per-currency amounts.
        </p>
      </KbSection>

      <VisibleTo user={user} when={u => !!u?.canEditCosts}>
        <KbSection title="Adding and editing lines" delay={2}>
          <StepList steps={[
            <>Press <strong>Add cost</strong> on the Costs tab.</>,
            <>Enter the amount (it must be above zero), currency, category, the date incurred and optional notes — an invoice reference or settlement breakdown.</>,
            <>Save. To correct a line later, use <strong>Edit</strong> on its row.</>,
          ]} />
        </KbSection>

        <KbSection title="Deleting a line" delay={3}>
          <p>
            <strong>Delete</strong> on a row removes it from the totals and the case card after
            a confirmation — and the deletion itself is recorded in the case history, so a
            removed cost is never silently gone.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function NotificationsYouReceiveBody() {
  return (
    <>
      <KbSection title="Why you might get one">
        <p>
          The tracker emails people automatically when something they're responsible for
          happens: a new case submitted, a status or severity change, a sign-off they requested
          being completed, or a colleague choosing to notify them from a case comment. Which
          events reach which people is decided by rules your administrators set up — so if
          an email arrives, you're someone who should know about or act on that case.
        </p>
        <p>
          <strong>Response-clock reminders</strong> are the other kind: when a turn on a coded
          treatment case runs past its deadline, the side whose turn it is gets a reminder. Replying
          on the case, or acknowledging the message, is what stops them — see <em>Response SLA and
          acknowledgements</em>.
        </p>
      </KbSection>

      <KbSection title="What's in it — and what never is" delay={1}>
        <p>
          Every message is deliberately minimal: the case reference (e.g. AE-042), the clinic,
          a one-line reason ("Status changed", "Document requested") and a button to open the
          case in the tracker.
        </p>
        <Callout tone="info">
          Client names and clinical details never appear in any email — that's a hard rule, not
          an oversight. The full case lives behind sign-in.
        </Callout>
      </KbSection>

      <KbSection title="Following the link" delay={2}>
        <p>
          The button opens the case after your normal Microsoft sign-in, and what you see there
          is exactly your usual access level — the email grants nothing extra. If a colleague
          asks about a notification, point them at the case link rather than forwarding details
          by email.
        </p>
      </KbSection>
    </>
  );
}

export const WORKING_CASES_ARTICLES = [
  {
    id: 'comments',
    title: 'Case comments',
    category: 'working-cases',
    summary: 'The running conversation on every case — reading the thread and posting to it.',
    keywords: ['comment', 'thread', 'discussion', 'post', 'conversation', 'reply', 'notify'],
    visibleTo: () => true,
    Body: CommentsBody,
  },
  {
    id: 'response-sla',
    title: 'Response SLA and acknowledgements',
    category: 'working-cases',
    summary: 'The response clock between Supervisors and clinics — whose turn it is, Acknowledge, holds, snooze and breaches.',
    keywords: ['sla', 'acknowledge', 'acknowledged', 'clock', 'response', 'turn', 'snooze', 'reminder', 'breach', 'waiting on supervisor', 'manage'],
    visibleTo: () => true,
    Body: ResponseSlaBody,
  },
  {
    id: 'private-comments',
    title: 'Private comments',
    category: 'working-cases',
    summary: 'Keep a note visible to HoRO, Admin and medical roles only.',
    keywords: ['chat', 'private', 'internal', 'visibility', 'comment', 'confidential'],
    // Gate on the actual private-content capability, not a role-name blocklist:
    // only roles that can post AND see private comments (Supervisor/SuperAdmin/
    // Admin/HoRO). Clinic roles (ClinicManager, ClinicMailbox / FOH) have
    // privateView=false, so they never see this — matching what the server enforces.
    visibleTo: (u) => !!u?.canComment && !!u?.canSeePrivateComments,
    Body: PrivateCommentsBody,
  },
  {
    id: 'edit-case-file',
    title: 'Editing the case file',
    category: 'working-cases',
    summary: 'Update case details after submission — panel Edit buttons, status and severity.',
    keywords: ['edit', 'update', 'change', 'correct', 'details', 'status', 'severity', 'notes'],
    visibleTo: (u) => !!u?.canWriteCases && u?.role !== 'ClinicManager',
    Body: EditCaseFileBody,
  },
  {
    id: 'attachments',
    title: 'Attachments & required documents',
    category: 'working-cases',
    summary: 'Where files live on a case, automatic file names, previews, limits and deleting.',
    keywords: ['attachment', 'file', 'upload', 'photo', 'document', 'slot', 'preview', 'delete', 'consent'],
    visibleTo: () => true,
    Body: AttachmentsBody,
  },
  {
    id: 'requirements',
    title: 'Managing the requirements checklist',
    category: 'working-cases',
    summary: 'Request documents and sign-offs on a case, and remove ones no longer needed.',
    keywords: ['requirement', 'checklist', 'sign-off', 'request', 'document', 'slot', 'training certificate'],
    visibleTo: (u) => !!u?.canManageRequirements,
    Body: RequirementsBody,
  },
  {
    id: 'tags',
    title: 'Tags',
    category: 'working-cases',
    summary: 'Label cases for follow-up and filtering — and what the locked system tags mean.',
    keywords: ['tag', 'label', 'filter', 'pill', 'sp_migrated', 'system tag', 'follow-up'],
    visibleTo: () => true,
    Body: TagsBody,
  },
  {
    id: 'resolve-close',
    title: 'Resolving a case',
    category: 'working-cases',
    summary: 'Close a case properly — the Mark resolved flow and what changes afterwards.',
    keywords: ['resolve', 'close', 'closed', 'resolution', 'outcome', 'mark resolved'],
    visibleTo: (u) => !!u?.canResolveCase,
    Body: ResolveCloseBody,
  },
  {
    id: 'reopen-delete-restore',
    title: 'Reopening, deleting and restoring',
    category: 'working-cases',
    summary: 'Reopen a closed case, soft-delete one submitted in error, restore from the bin.',
    keywords: ['reopen', 'delete', 'restore', 'bin', 'deleted', 'undo', 'closed', '90 days'],
    visibleTo: (u) => !!u?.canReopenCase || !!u?.canDeleteCase,
    Body: ReopenDeleteRestoreBody,
  },
  {
    id: 'costs',
    title: 'Tracking costs',
    category: 'working-cases',
    summary: 'Record what a case costs — line items, category pills and per-currency totals.',
    keywords: ['cost', 'refund', 'goodwill', 'money', 'total', 'currency', 'expense'],
    visibleTo: (u) => !!u?.canSeeCosts,
    Body: CostsBody,
  },
  {
    id: 'notifications-you-receive',
    title: 'Emails the tracker sends',
    category: 'working-cases',
    summary: 'Why you get case emails, what they contain — and what they deliberately leave out.',
    keywords: ['email', 'notification', 'notify', 'message', 'inbox', 'alert'],
    visibleTo: () => true,
    Body: NotificationsYouReceiveBody,
  },
];
